import React, { useEffect, useRef, useState } from "react";

import PencilIconSrc from "../../resources/pencil.svg";

function PencilIcon({ className = "" }: { className?: string }) {
  return <img src={PencilIconSrc} alt="" className={className} aria-hidden />;
}

export default function EditableText({
  value,
  onSave,
  editLabel = "Edit",
  className = "",
  testId,
}: {
  value: string;
  onSave: (next: string) => void;
  editLabel?: string;
  className?: string;
  testId?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    const trimmed = draft.trim();
    setEditing(false);
    if (!trimmed || trimmed === value) {
      setDraft(value);
      return;
    }
    onSave(trimmed);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        data-testid={testId ? `${testId}-input` : undefined}
        className={`min-w-0 flex-1 rounded border border-sky-300 bg-white px-1.5 py-0.5 text-sm font-semibold text-snow-primary outline-none ring-1 ring-sky-200 ${className}`}
        value={draft}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
          if (e.key === "Escape") {
            setDraft(value);
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <div
      className={`group/name flex min-w-0 flex-1 items-center gap-1.5 ${className}`}
    >
      <span
        data-testid={testId}
        className="truncate font-semibold text-snow-primary"
      >
        {value}
      </span>
      <button
        type="button"
        data-testid={testId ? `${testId}-edit` : undefined}
        title={editLabel}
        aria-label={editLabel}
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 transition hover:bg-slate-100 hover:text-sky-700 focus-visible:bg-slate-100 focus-visible:text-sky-700"
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
      >
        <PencilIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
